import * as coreCommon from 'assets/core/js/common'
import type { LngLatBounds, LngLatBoundsLike, Map as MapLibreMap, MapOptions, Marker, NavigationControl } from 'maplibre-gl'

export interface MapConfig {
  onLoad?(): void
  onClose?: () => void
  closeButton?: boolean
}

export interface MapMarker {
  id?: string
  overlayText?: string
  content?: string
  latitude: number
  longitude: number
}

export default class DynamicMap {
  element!: HTMLElement
  isInitialized = false
  markers!: Marker[]
  config!: MapConfig
  mapLatitude!: string | null
  mapLongitude!: string | null
  mapBoundariesBox!: LngLatBoundsLike
  map!: MapLibreMap
  bounds!: LngLatBounds
  private Marker!: typeof Marker
  private LngLatBounds!: typeof LngLatBounds

  constructor(element: string | HTMLElement, config: MapConfig, markers: MapMarker[]) {
    let el: HTMLElement | string | null = element

    if (typeof element === 'string') {
      el = document.querySelector<HTMLElement>(element)
    }

    if (!el || typeof el === 'string') {
      return
    }

    this.element = el
    this.markers = []
    this.config = config ?? {}

    if (!this.element) {
      return
    }

    this.initMap(markers).catch((error) => {
      console.error('Failed to initialize map:', error)
    })
  }

  async initMap(markers: MapMarker[]): Promise<void> {
    const loaderEl = this.element.querySelector<HTMLElement>('.dca-loader')
    const maplibregl = await import('maplibre-gl')

    // @ts-expect-error css import is handled by webpack
    await import('maplibre-gl/dist/maplibre-gl.css')

    const { LngLatBounds, Map: MapLibreMap, Marker, NavigationControl } = maplibregl

    this.Marker = Marker
    this.LngLatBounds = LngLatBounds

    this.mapLatitude = this.element.getAttribute('data-latitude')
    this.mapLongitude = this.element.getAttribute('data-longitude')

    const styleUrl = new URL(this.element.getAttribute('data-style-url') as string)

    const mapConfig: MapOptions = {
      container: this.element,
      zoom: Number(this.element.getAttribute('data-zoom')),
      center: [0, 0],
      renderWorldCopies: false,
      // to enable raster:
      // style: {
      //   version: 8,
      //   sources: {
      //     'simple-tiles': {
      //       type: 'raster',
      //       tiles: ['https://qa.www.campings.com/maps/tile/512/{z}/{x}/{y}.png'],
      //       tileSize: 512,
      //       attribution: 'MapLibre | &copy; OpenStreetMap',
      //     },
      //   },
      //   layers: [
      //     {
      //       id: 'simple-tiles',
      //       type: 'raster',
      //       source: 'simple-tiles',
      //       minzoom: 0,
      //       maxzoom: 22,
      //     },
      //   ],
      // },
      style: styleUrl.toString(),
      // since we can't rewrite the tile server paths in the infra, we have to tweak the map lib requests so they actually go through
      transformRequest: (url) => {
        const prefix = '/maps/assets'

        if (url.startsWith(styleUrl.origin)) {
          const path = url.replace(styleUrl.origin, '')

          if (!path.startsWith(prefix)) {
            return {
              url: `${styleUrl.origin}${prefix}${path}`,
            }
          }
        }

        return { url }
      },
    }

    const mapBoundariesBox = this.element.getAttribute('data-map-boundaries')

    if (mapBoundariesBox) {
      this.mapBoundariesBox = JSON.parse<LngLatBoundsLike>(mapBoundariesBox)
      mapConfig.bounds = this.mapBoundariesBox
    }

    if (this.mapLatitude && this.mapLongitude) {
      mapConfig.center = [parseFloat(this.mapLongitude), parseFloat(this.mapLatitude)]
    }

    const closeButtonLabel = this.element.hasAttribute('data-close-label') ? (this.element.getAttribute('data-close-label') as string) : ''

    const CloseControl = function (this: NavigationControl, callback: MapConfig['onClose']): NavigationControl {
      let container: HTMLElement | undefined

      this.onAdd = function () {
        container = document.createElement('div')
        container.className = 'maplibregl-ctrl maplibregl-ctrl-group'

        const button = document.createElement('button')
        button.className = 'maplibregl-ctrl-zoom-in'
        button.type = 'button'
        button.innerHTML = '<span aria-hidden="true">&times;</span>'
        button.title = closeButtonLabel
        button.setAttribute('data-type', 'close')

        if (callback) {
          button.addEventListener('click', callback)
        }

        container.appendChild(button)
        return container
      }

      this.onRemove = function () {
        container?.parentNode?.removeChild(container)
      }

      return this
    }

    this.map = new MapLibreMap(mapConfig)
    loaderEl?.remove()

    if (this.config?.closeButton && this.config.onClose) {
      // @ts-expect-error to check later
      this.map.addControl(new CloseControl(this.config.onClose))
    }

    this.map.addControl(new NavigationControl())
    setTimeout(() => this.resize(), 0)

    this.map.on('load', () => {
      this.isInitialized = true
      this.element.dispatchEvent(new CustomEvent('map-initialized'))

      if (this.config.onLoad) {
        this.config.onLoad()
      }
    })

    window.addEventListener('resize', () => {
      setTimeout(() => {
        this.map.resize()
        this.fitBounds()
      }, 250)
    })

    if (markers) {
      this.setMarkers(markers)
    }
  }

  resize(): MapLibreMap {
    return this.map.resize()
  }

  setMarkers(markers: MapMarker[]): DynamicMap {
    this.clearMarkers()

    markers.forEach((marker) => {
      this.setMarker(marker)
    })

    if (!this.mapLatitude && !this.mapLongitude && this.markers.length > 0 && !this.mapBoundariesBox) {
      this.fitBounds()
    }

    return this
  }

  clearMarkers(): DynamicMap {
    for (let i = 0, j = this.markers.length; i < j; i++) {
      this.markers[i]?.remove()
    }

    this.markers = []

    return this
  }

  setMarker(markerConfig: MapMarker): Marker | boolean {
    if (markerConfig.latitude && markerConfig.longitude) {
      const markerImage = markerConfig.content
        ? markerConfig.content
        : this.element.hasAttribute('data-marker-image')
          ? (this.element.getAttribute('data-marker-image') as string)
          : null

      const markerTemplate = `<div class="marker-wrapper">
        <div class="marker-icon">${markerImage !== null ? `<img src="${markerImage}" loading="lazy" height="48" width="48" alt="" />` : ''}</div>
      </div>`

      const marker = new this.Marker({
        element: coreCommon.createNodeFromHTML(markerTemplate),
        anchor: 'bottom',
      })
        .setLngLat([markerConfig.longitude, markerConfig.latitude])
        .addTo(this.map)

      this.markers.push(marker)

      return marker
    }

    return false
  }

  fitBounds(): DynamicMap {
    this.bounds = new this.LngLatBounds()

    this.markers.forEach((marker) => {
      this.bounds?.extend(marker.getLngLat())
    })

    this.map.easeTo({
      center: this.bounds.getCenter(),
    })

    return this
  }

  getMap(): MapLibreMap {
    return this.map
  }
}
